import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Heading, PageHead, Switch, Text } from '../ui/index.js';
import { MUTABLE_NOTIFICATION_TYPES } from '@goodbuild/shared';
import { api } from '../api/client.js';
import { useAccess } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { Loading } from '../components/layout/Loading.js';
import { useSettings } from '../lib/settings.js';
import { toggleBigPicture, useBigPicture, type BigPictureLaunch } from '../lib/bigPicture.js';
import { useTrayAvailable } from '../lib/tray.js';
import { capabilities, isDesktop } from '../platform/index.js';
import { native, type LibraryFolder } from '../platform/tauri.js';
import { formatBytes } from '../lib/format.js';
import type { GlyphSet } from '../gamepad/glyphs.js';
import { NOTIFICATION_TYPE_LABELS } from '../lib/notificationLabels.js';
import { LEGAL } from '../lib/legal.js';

export const Route = createFileRoute('/settings')({ component: SettingsPage });

const SPEED_OPTIONS: Array<{ label: string; value: number | null }> = [
  { label: 'noLimit', value: null },
  { label: '1 MB/s', value: 1_000_000 },
  { label: '5 MB/s', value: 5_000_000 },
  { label: '10 MB/s', value: 10_000_000 },
  { label: '25 MB/s', value: 25_000_000 },
];

const GLYPH_OPTIONS: Array<GlyphSet | 'auto'> = ['auto', 'xbox', 'playstation', 'steamdeck'];

const BIG_PICTURE_OPTIONS: BigPictureLaunch[] = ['auto', 'on', 'off'];

/**
 * What each launch option means, said plainly.
 *
 * `auto` names the machine rather than the rule, because "on for Linux" is what somebody needs
 * to know and "follow the platform" is what they would have to work out.
 */
const BIG_PICTURE_LABELS: Record<BigPictureLaunch, string> = {
  auto: 'Automatic',
  on: 'Always',
  off: 'Never',
};

const GLYPH_LABELS: Record<GlyphSet | 'auto', string> = {
  auto: 'Match my controller',
  xbox: 'Xbox',
  playstation: 'PlayStation',
  steamdeck: 'Steam Deck',
};

function SettingsPage() {
  const navigate = useNavigate();
  const { signedIn } = useAccess();
  const settings = useSettings();
  const trayAvailable = useTrayAvailable();
  const bigPicture = useBigPicture();
  const qc = useQueryClient();
  const [folders, setFolders] = React.useState<LibraryFolder[]>([]);

  React.useEffect(() => {
    if (!isDesktop) return;
    native
      .libraryFoldersList()
      .then(setFolders)
      .catch(() => setFolders([]));
  }, []);

  const muted = useQuery({
    queryKey: ['notifications', 'settings'],
    queryFn: () => api.get<{ available: string[]; muted: string[] }>('/notifications/settings'),
    enabled: signedIn,
  });

  const saveMuted = useMutation({
    mutationFn: (next: string[]) => api.put('/notifications/settings', { muted: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'settings'] }),
  });

  const toggleMuted = (type: string) => {
    const current = muted.data?.muted ?? [];
    const next = current.includes(type) ? current.filter((m) => m !== type) : [...current, type];
    settings.update({ mutedNotifications: next });
    saveMuted.mutate(next);
  };

  const addFolder = async () => {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const picked = await open({ directory: true, multiple: false });
    if (typeof picked === 'string') setFolders(await native.libraryFoldersAdd(picked));
  };

  return (
    <FocusZone focusKey="settings" direction="column" className="gb-stack">
      <PageHead
        eyebrow="This device"
        title="Settings."
        description="How this install behaves. These live here, not on your account."
      />

      {isDesktop ? (
        <Card size="3" className="gb-stack gb-stack--tight">
          <Heading size="4">Library folders</Heading>
          {folders.map((folder) => (
            <div key={folder.path} className="gb-seat">
              <Text size="2">{folder.path}</Text>
              <span className="gb-seat__spacer" />
              <Text size="1" color="gray">
                {`${formatBytes(folder.freeBytes)} free of ${formatBytes(folder.totalBytes)}`}
              </Text>
              <Focusable
                onSelect={() => void native.libraryFoldersRemove(folder.path).then(setFolders)}
                selectLabel="Remove"
                disabled={folder.installedGames > 0}
              >
                <Button size="1" variant="ghost" disabled={folder.installedGames > 0}>
                  Remove
                </Button>
              </Focusable>
            </div>
          ))}
          <Focusable autoFocus onSelect={() => void addFolder()} selectLabel="Add a folder">
            <Button size="2">Add a folder</Button>
          </Focusable>
        </Card>
      ) : null}

      {capabilities.installWindowsGames || capabilities.installAndroidGames ? (
        <Card size="3">
          <Heading size="4">Downloads</Heading>

          <Text size="2" color="gray">
            Download speed limit
          </Text>
          <div className="gb-segmented" role="group" aria-label="Download speed limit">
            {SPEED_OPTIONS.map((option) => (
              <Focusable
                key={option.label}
                onSelect={() => {
                  settings.update({ downloadLimitBps: option.value });
                  void native.downloadsSetLimit(option.value).catch(() => {});
                }}
                selectLabel={option.value === null ? 'No limit' : option.label}
                className="gb-focusable--flat"
              >
                <button
                  type="button"
                  className="gb-segmented__item"
                  aria-pressed={settings.downloadLimitBps === option.value}
                >
                  {option.value === null ? 'No limit' : option.label}
                </button>
              </Focusable>
            ))}
          </div>

          <Text size="2" color="gray">
            Parallel downloads
          </Text>
          <div className="gb-segmented" role="group" aria-label="Parallel downloads">
            {[4, 8, 16, 32].map((value) => (
              <Focusable
                key={value}
                onSelect={() => settings.update({ downloadConcurrency: value })}
                selectLabel={String(value)}
                className="gb-focusable--flat"
              >
                <button
                  type="button"
                  className="gb-segmented__item"
                  aria-pressed={settings.downloadConcurrency === value}
                >
                  {value}
                </button>
              </Focusable>
            ))}
          </div>

          <div className="gb-stack gb-stack--tight" style={{ marginTop: '0.75rem' }}>
            <Focusable
              onSelect={() => settings.update({ autoUpdate: !settings.autoUpdate })}
              selectLabel="Update games automatically"
              className="gb-focusable--flat"
            >
              <div className="gb-seat">
                <Text size="2">{'Update games automatically'}</Text>
                <span className="gb-seat__spacer" />
                <Switch on={settings.autoUpdate} />
              </div>
            </Focusable>
            <Text render={<p />} size="1" color="gray">
              Games with an update cannot be launched until they are updated.
            </Text>
          </div>

          {/* Only where there is a tray to close into. A desktop without one — a Steam Deck in
              Gaming Mode — closes the window either way, and a switch that does nothing is
              worse than no switch. */}
          {trayAvailable ? (
            <div className="gb-stack gb-stack--tight" style={{ marginTop: '0.75rem' }}>
              <Focusable
                onSelect={() => settings.update({ closeToTray: !settings.closeToTray })}
                selectLabel="Keep running when the window closes"
                className="gb-focusable--flat"
              >
                <div className="gb-seat">
                  <Text size="2">{'Keep running when the window closes'}</Text>
                  <span className="gb-seat__spacer" />
                  <Switch on={settings.closeToTray} />
                </div>
              </Focusable>
              <Text render={<p />} size="1" color="gray">
                Downloads and app updates carry on from the tray icon. Quit from there to stop them.
              </Text>
            </div>
          ) : null}

          {capabilities.installAndroidGames ? (
            <div className="gb-stack gb-stack--tight" style={{ marginTop: '0.75rem' }}>
              <Focusable
                onSelect={() => settings.update({ keepAndroidApk: !settings.keepAndroidApk })}
                selectLabel="Keep the installed APK"
                className="gb-focusable--flat"
              >
                <div className="gb-seat">
                  <Text size="2">{'Keep the installed APK'}</Text>
                  <span className="gb-seat__spacer" />
                  <Switch on={settings.keepAndroidApk} />
                </div>
              </Focusable>
              <Text render={<p />} size="1" color="gray">
                Uses more space, and makes the next update much smaller.
              </Text>
            </div>
          ) : null}
        </Card>
      ) : null}

      {/* Big Picture is the console across a room (`03-client-app.md` section 4.6). It comes
          before the wallpaper and the motion cards because it is the mode those describe. */}
      {capabilities.bigPicture ? (
        <Card size="3" className="gb-stack gb-stack--tight">
          <Heading size="4">Big Picture</Heading>
          <Focusable
            onSelect={toggleBigPicture}
            selectLabel={bigPicture ? 'Leave Big Picture' : 'Big Picture'}
            className="gb-focusable--flat"
          >
            <div className="gb-seat">
              <Text size="2">{'Big Picture now'}</Text>
              <span className="gb-seat__spacer" />
              <Switch on={bigPicture} />
            </div>
          </Focusable>
          <Text render={<p />} size="1" color="gray">
            {
              'The whole screen, scaled to be read from across a room, with the controller hints always up. F11 anywhere in the app does the same.'
            }
          </Text>

          <Text size="2" color="gray">
            Open in Big Picture
          </Text>
          <div className="gb-segmented" role="group" aria-label="Open in Big Picture">
            {BIG_PICTURE_OPTIONS.map((option) => (
              <Focusable
                key={option}
                onSelect={() => settings.update({ bigPictureOnLaunch: option })}
                selectLabel={BIG_PICTURE_LABELS[option]}
                className="gb-focusable--flat"
              >
                <button
                  type="button"
                  className="gb-segmented__item"
                  aria-pressed={settings.bigPictureOnLaunch === option}
                >
                  {BIG_PICTURE_LABELS[option]}
                </button>
              </Focusable>
            ))}
          </div>
          <Text render={<p />} size="1" color="gray">
            {
              'Automatic opens in Big Picture on Linux, where this runs from a Steam Deck in Gaming Mode, and in a window on Windows.'
            }
          </Text>
        </Card>
      ) : null}

      {/* The wallpaper is the room the console sits in, and on a handheld the room is work the
          GPU is doing for something nobody is looking at. So it is a control. */}
      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">Background</Heading>
        <Focusable
          onSelect={() => settings.update({ ambientArt: !settings.ambientArt })}
          selectLabel="Artwork behind the app"
          className="gb-focusable--flat"
        >
          <div className="gb-seat">
            <Text size="2">{'Artwork behind the app'}</Text>
            <span className="gb-seat__spacer" />
            <Switch on={settings.ambientArt} />
          </div>
        </Focusable>
        <Text render={<p />} size="1" color="gray">
          {'Whatever is on screen, blurred back behind everything. Off leaves the shipped one.'}
        </Text>
      </Card>

      {/* Off here means off at the source: nothing is queued and nothing already queued is
          kept, so this is not a pause button and is not described as one. */}
      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">Usage data</Heading>
        <Focusable
          onSelect={() => settings.update({ shareUsageData: !settings.shareUsageData })}
          selectLabel="Share usage data"
          className="gb-focusable--flat"
        >
          <div className="gb-seat">
            <Text size="2">{'Share usage data'}</Text>
            <span className="gb-seat__spacer" />
            <Switch on={settings.shareUsageData} />
          </div>
        </Focusable>
        <Text render={<p />} size="1" color="gray">
          {
            'Which screens get opened, which games get installed and how long they run. No identifier is sent with it, it goes to our own Cloudflare account and nowhere else, and it is how we find out that a game is being installed and never played. See the privacy policy.'
          }
        </Text>
      </Card>

      {/* The brand guide treats motion as a control, not an accessibility afterthought: the
          outline and the state labels stay, only the movement goes. */}
      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">Motion</Heading>
        <Focusable
          onSelect={() => settings.update({ reduceMotion: !settings.reduceMotion })}
          selectLabel="Reduce motion"
          className="gb-focusable--flat"
        >
          <div className="gb-seat">
            <Text size="2">{'Reduce motion'}</Text>
            <span className="gb-seat__spacer" />
            <Switch on={settings.reduceMotion} />
          </div>
        </Focusable>
        <Text render={<p />} size="1" color="gray">
          {'Drops every transition and the carousel. Focus outlines and state labels stay.'}
        </Text>
      </Card>

      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">Controller</Heading>
        <Text size="2" color="gray">
          Button labels
        </Text>
        <div className="gb-segmented" role="group" aria-label="Button labels">
          {GLYPH_OPTIONS.map((option) => (
            <Focusable
              key={option}
              onSelect={() => settings.update({ glyphOverride: option })}
              selectLabel={GLYPH_LABELS[option]}
              className="gb-focusable--flat"
            >
              <button
                type="button"
                className="gb-segmented__item"
                aria-pressed={settings.glyphOverride === option}
              >
                {GLYPH_LABELS[option]}
              </button>
            </Focusable>
          ))}
        </div>
      </Card>

      {signedIn ? (
        <Card size="3">
          <Heading size="4">Notifications</Heading>
          {muted.isLoading ? (
            <Loading />
          ) : (
            <div className="gb-stack">
              {MUTABLE_NOTIFICATION_TYPES.map((type) => {
                const off = (muted.data?.muted ?? []).includes(type);
                return (
                  <Focusable
                    key={type}
                    onSelect={() => toggleMuted(type)}
                    selectLabel={NOTIFICATION_TYPE_LABELS[type]}
                  >
                    <div className="gb-seat">
                      <Text size="2">{NOTIFICATION_TYPE_LABELS[type]}</Text>
                      <span className="gb-seat__spacer" />
                      <Switch on={!off} onLabel="On" offLabel="Muted" />
                    </div>
                  </Focusable>
                );
              })}
            </div>
          )}
        </Card>
      ) : null}

      {/* Every screen is reachable from the rail except these two, so this is where they live.
          A legal page nobody can find is a legal page that does not exist. */}
      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">{'Legal'}</Heading>
        <div className="gb-row">
          <Focusable
            onSelect={() => void navigate({ to: '/terms' })}
            selectLabel="Terms of service"
          >
            <Button size="2" variant="surface">
              {'Terms of service'}
            </Button>
          </Focusable>
          <Focusable
            onSelect={() => void navigate({ to: '/privacy' })}
            selectLabel="Privacy policy"
          >
            <Button size="2" variant="surface">
              {'Privacy policy'}
            </Button>
          </Focusable>
        </div>
        <Text size="1" color="gray">
          {`${LEGAL.brand} is operated by ${LEGAL.entity}, ${LEGAL.address}. NIP ${LEGAL.nip}.`}
        </Text>
      </Card>
    </FocusZone>
  );
}
